import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CalendarPlus, MessageCircle, CheckCircle2 } from 'lucide-react';
import { useTrackingPixel } from '../hooks/use-tracking-pixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ThankYou() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ date: '', time: '', zoom_link: '' });
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const userName = sessionStorage.getItem('registered_name') || 'there';

  useEffect(() => {
    fetch(`${API_URL}/api/settings`)
      .then(r => r.json())
      .then(data => setSettings(data))
      .catch(() => {});
  }, []);

  useTrackingPixel(settings.tracking_pixel);

  // Countdown timer — tries to parse a date from the settings
  useEffect(() => {
    const parseTarget = () => {
      const dateStr = settings.date || '';
      // Extract day, month, year from formats like "21st May 2026 (Thursday)"
      const match = dateStr.match(/(\d+)\w*\s+(\w+)\s+(\d{4})/);
      if (!match) return null;
      const timeStr = settings.time || '8.00PM';
      const hourMatch = timeStr.match(/(\d+)\.?(\d*)\s*(AM|PM)/i);
      let hours = 20;
      if (hourMatch) {
        hours = parseInt(hourMatch[1]);
        if (hourMatch[3].toUpperCase() === 'PM' && hours !== 12) hours += 12;
        if (hourMatch[3].toUpperCase() === 'AM' && hours === 12) hours = 0;
      }
      const target = new Date(`${match[2]} ${match[1]}, ${match[3]} ${hours}:00:00`);
      return isNaN(target.getTime()) ? null : target;
    };

    const target = parseTarget();
    if (!target) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = target - now;
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(interval);
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [settings]);

  const googleCalUrl = () => {
    const dateStr = settings.date || '';
    const match = dateStr.match(/(\d+)\w*\s+(\w+)\s+(\d{4})/);
    if (!match) return '#';
    const timeStr = settings.time || '8.00PM';
    const hourMatch = timeStr.match(/(\d+)\.?(\d*)\s*(AM|PM)/i);
    let hours = 20, mins = 0;
    if (hourMatch) {
      hours = parseInt(hourMatch[1]);
      mins = hourMatch[2] ? parseInt(hourMatch[2]) : 0;
      if (hourMatch[3].toUpperCase() === 'PM' && hours !== 12) hours += 12;
    }
    const d = new Date(`${match[2]} ${match[1]}, ${match[3]} ${hours}:${mins}:00`);
    if (isNaN(d.getTime())) return '#';
    const pad = (n) => String(n).padStart(2, '0');
    const start = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(hours)}${pad(mins)}00`;
    const endH = hours + 2;
    const end = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(endH)}${pad(mins)}00`;
    const details = settings.zoom_link ? `Join Zoom: ${settings.zoom_link}` : '';
    return `https://calendar.google.com/calendar/r/eventedit?text=Signature+of+Wealth+Webinar&dates=${start}/${end}&details=${encodeURIComponent(details)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      {/* Hero */}
      <div className="pt-16 pb-12 text-center px-4">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-[#F5A623]" />
          </div>
        </div>
        <h1 data-testid="thankyou-heading" className="text-5xl sm:text-6xl font-black italic mb-4">
          CONGRATULATIONS!
        </h1>
        <h2 className="text-3xl sm:text-4xl font-black italic text-[#F5A623] mb-8">
          YOUR SEAT IS SECURED!
        </h2>
        <p className="text-xl text-gray-300 mb-2">{settings.date || 'Loading...'}</p>
        <p className="text-lg text-gray-400">{settings.time || ''}</p>
      </div>

      {/* Countdown */}
      <div className="max-w-2xl mx-auto px-4 mb-16">
        <div className="grid grid-cols-4 gap-4 text-center">
          {[
            { val: countdown.days, label: 'Days' },
            { val: countdown.hours, label: 'Hours' },
            { val: countdown.minutes, label: 'Minutes' },
            { val: countdown.seconds, label: 'Seconds' }
          ].map((item) => (
            <div key={item.label} className="bg-[#1A1A1D] border border-white/10 rounded-xl py-5">
              <p className="text-4xl sm:text-5xl font-black text-[#F5A623]">{item.val}</p>
              <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Important Note */}
      <div className="max-w-3xl mx-auto px-4 mb-16">
        <div className="bg-[#1A1A1D] border border-[#F5A623]/30 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold mb-3">
            IMPORTANT NOTE:
          </h3>
          <p className="text-gray-300 leading-relaxed">
            To ensure you get the best experience in this class, do go through the following steps below...
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="max-w-3xl mx-auto px-4 pb-20 space-y-12">
        {/* Step 1 */}
        <div className="bg-[#1A1A1D] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#F5A623] rounded-full flex items-center justify-center text-black font-bold text-lg">1</div>
            <h3 className="text-2xl font-bold">Save The Date In Google Calendar</h3>
          </div>
          <div className="space-y-4 text-gray-300 leading-relaxed mb-6">
            <p><strong className="text-white">Put this date into your smartphone:</strong></p>
            <p>Date: <strong className="text-[#F5A623]">{settings.date}</strong></p>
            <p>Time: <strong className="text-[#F5A623]">{settings.time}</strong></p>
            <p>I want to encourage you to do everything in your power to show up <strong className="text-white">30 minutes early</strong> before the start of each training session... so you don't miss a single moment of this sharing!</p>
          </div>
          <a href={googleCalUrl()} target="_blank" rel="noopener noreferrer">
            <Button data-testid="save-calendar-btn" className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold px-8 py-3">
              <CalendarPlus className="w-5 h-5 mr-2" /> Add to Google Calendar
            </Button>
          </a>
        </div>

        {/* Step 2 */}
        <div className="bg-[#1A1A1D] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#F5A623] rounded-full flex items-center justify-center text-black font-bold text-lg">2</div>
            <h3 className="text-2xl font-bold">Join Our WhatsApp Community</h3>
          </div>
          <div className="space-y-4 text-gray-300 leading-relaxed mb-6">
            <p>Join our exclusive WhatsApp group so you don't miss out on any important updates and content about the event.</p>
            <p>Simply join by clicking the link below. <strong className="text-white">I can't wait to see you there.</strong></p>
          </div>
          <a href="https://chat.whatsapp.com/Ksy6CLUw9zPBUNBHXTW19i" target="_blank" rel="noopener noreferrer">
            <Button data-testid="join-whatsapp-btn" className="bg-[#25D366] hover:bg-[#1EBE57] text-white font-bold px-8 py-3">
              <MessageCircle className="w-5 h-5 mr-2" /> Join Our WhatsApp Community
            </Button>
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8 px-4 text-center">
        <p className="text-gray-500 text-sm mb-4">COPYRIGHT &copy; Steven Yong</p>
        <p className="text-gray-600 text-xs max-w-3xl mx-auto leading-relaxed">
          Disclaimer: Earning potential is entirely dependent on the person using our product, ideas and techniques. We do not purport this as a "get rich scheme." Your level of success in attaining the results claimed in our materials depends on the time you devote to the program, ideas and techniques mentioned, your finances, knowledge and various skills.
        </p>
      </div>
    </div>
  );
}
